import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  Car, CalendarDays, DollarSign, MessageSquare, 
  TrendingUp, ChevronRight, Clock 
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [statsRes, bookingsRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/dashboard/stats`, { headers }),
          axios.get(`${API_URL}/api/admin/bookings`, { headers })
        ]);
        setStats(statsRes.data);
        setRecentBookings(bookingsRes.data.slice(0, 5));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const statCards = [
    {
      title: 'Total Vehicles',
      value: stats?.vehicles?.total || 0,
      subtitle: `${stats?.vehicles?.available || 0} available`,
      icon: Car,
      color: 'text-blue-400',
      bgColor: 'bg-blue-400/10'
    },
    {
      title: 'Total Bookings',
      value: stats?.bookings?.total || 0,
      subtitle: `${stats?.bookings?.pending || 0} pending`,
      icon: CalendarDays,
      color: 'text-green-400',
      bgColor: 'bg-green-400/10'
    },
    {
      title: 'Total Revenue',
      value: `CHF ${(stats?.revenue?.total || 0).toLocaleString()}`,
      subtitle: 'All time',
      icon: DollarSign,
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      title: 'Inquiries',
      value: stats?.inquiries?.total || 0,
      subtitle: 'Fleet & Long-term',
      icon: MessageSquare,
      color: 'text-purple-400',
      bgColor: 'bg-purple-400/10'
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      confirmed: 'bg-blue-500/20 text-blue-400',
      delivered: 'bg-green-500/20 text-green-400',
      returned: 'bg-gray-500/20 text-gray-400',
      cancelled: 'bg-red-500/20 text-red-400'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Header */}
      <div>
        <h1 className="font-serif text-3xl text-[#181B1F] font-light">Dashboard</h1>
        <p className="text-gray-500 mt-1">Welcome back! Here's your overview.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className="bg-white border-gray-100 hover:border-gray-200 transition-colors">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">{stat.title}</p>
                    <p className="text-2xl text-[#181B1F] font-light mt-1">{stat.value}</p>
                    <p className="text-gray-400 text-xs mt-1">{stat.subtitle}</p>
                  </div>
                  <div className={`w-10 h-10 ${stat.bgColor} rounded-sm flex items-center justify-center`}>
                    <stat.icon className={`w-5 h-5 ${stat.color}`} strokeWidth={1.5} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Recent Bookings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card className="bg-white border-gray-100">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-serif text-xl text-[#181B1F] font-light">
              Recent Bookings
            </CardTitle>
            <Link 
              to="/admin/bookings"
              className="text-primary text-sm flex items-center gap-1 hover:underline"
            >
              View all
              <ChevronRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {recentBookings.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No bookings yet</p>
            ) : (
              <div className="space-y-4">
                {recentBookings.map((booking) => (
                  <div 
                    key={booking.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-sm"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-gray-100 rounded-sm flex items-center justify-center">
                        <Car className="w-5 h-5 text-gray-500" strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="text-[#181B1F] font-medium">{booking.vehicle_name}</p>
                        <p className="text-gray-400 text-sm">
                          {booking.customer_info?.full_name} • {booking.pickup_date}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-[#181B1F] font-medium">
                        CHF {booking.total_price?.toFixed(2)}
                      </span>
                      <span className={`px-2 py-1 rounded-sm text-xs ${getStatusColor(booking.status)}`}>
                        {booking.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link to="/admin/vehicles">
          <Card className="bg-white border-gray-100 hover:border-primary/30 transition-colors cursor-pointer group">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <Car className="w-6 h-6 text-primary" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-[#181B1F] font-medium">Manage Vehicles</p>
                <p className="text-gray-400 text-sm">Add, edit or remove vehicles</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-300 ml-auto group-hover:text-primary transition-colors" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/bookings">
          <Card className="bg-white border-gray-100 hover:border-green-400/30 transition-colors cursor-pointer group">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-green-400/10 rounded-sm flex items-center justify-center group-hover:bg-green-400/20 transition-colors">
                <CalendarDays className="w-6 h-6 text-green-400" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-[#181B1F] font-medium">View Bookings</p>
                <p className="text-gray-400 text-sm">Manage reservations & reports</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-300 ml-auto group-hover:text-green-400 transition-colors" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/inquiries">
          <Card className="bg-white border-gray-100 hover:border-purple-400/30 transition-colors cursor-pointer group">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-400/10 rounded-sm flex items-center justify-center group-hover:bg-purple-400/20 transition-colors">
                <MessageSquare className="w-6 h-6 text-purple-400" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-[#181B1F] font-medium">View Inquiries</p>
                <p className="text-gray-400 text-sm">Fleet & long-term requests</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-300 ml-auto group-hover:text-purple-400 transition-colors" />
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
};

export default AdminDashboard;
